import {
  Query,
  QueryCache,
  QueryClient,
} from '@tanstack/react-query'
import axios, { AxiosError } from 'axios'
import {
  describeApiError,
  describeApiResource,
} from '@common/utils/apiErrors'
import { notifyError } from '@common/utils/notify'
import { InternalAxiosRequestConfigCustom } from '@projtypes/request'
import { retryOnlyTransient } from './retryPolicy'

// React Query v5 removed `onError` from queries, so a failed query was indistinguishable from
// an empty result: a 403 on /tickets just rendered an empty list. This reports query failures
// in one place. Mutations are not covered: they report through their own onError, and a
// MutationCache handler would double every one of those.
//
// Query hashes with a failure already reported. refetchOnWindowFocus re-fails every query on
// every refocus during an outage; one notification per failure run is readable, one per
// refocus is not. Cleared when the query next succeeds, so a later failure reports again.
const reportedFailures = new Set<string>()

const shouldReportQueryError = (error: AxiosError, query: Query<any, any, any, any>) => {
  if (reportedFailures.has(query.queryHash)) return false

  // Nobody is looking at this data, so a background failure is not worth interrupting for.
  if (query.getObserversCount() === 0) return false

  // An aborted signal is not a failure. Checked directly: getApiErrorMessage returns undefined
  // for this and for a 500 with a plain-text body alike, and that 500 must report.
  if (axios.isCancel(error) || error.code === 'ERR_CANCELED') return false

  // No 401 filter, deliberately (decided 2026-09-25). A 401 the interceptor refreshes never
  // reaches here: the retried request succeeds. A refresh that fails on a session error clears
  // the session, and clearSession's queryClient.clear() discards the query before it can
  // report. What is left does deserve a message: a refresh that failed for another reason
  // (a 500, a dropped connection), or an API still refusing the token after a refresh.
  return true
}

// The error a query rejects with when the token refresh itself failed. Its URL is the token
// endpoint, which describeApiResource would label 'users'.
const isRefreshFailure = (error: AxiosError) =>
  !!(error.config as InternalAxiosRequestConfigCustom | undefined)?._skipAuthRefresh

const fallbackMessage = (error: AxiosError) => {
  if (isRefreshFailure(error)) return 'Your session could not be refreshed'

  const resource = describeApiResource(error)

  return resource ? `Could not load ${resource}` : 'Could not load data'
}

const queryCache = new QueryCache({
  onError: (error, query) => {
    // notifyApiError's contract: an error that is not from axios is logged, not shown.
    if (!axios.isAxiosError(error)) {
      console.error(error)
      return
    }

    if (!shouldReportQueryError(error, query)) return

    reportedFailures.add(query.queryHash)

    // The 403 suffix already names the resource when the API gave a message; the fallback
    // names it for a response without one, e.g. 'Could not load tickets — Server error (500)'.
    const message = describeApiError(error, fallbackMessage(error))

    // Keyed on the text, not the query: several queries failing for one cause, such as a
    // 403 across one resource, show a single notification.
    notifyError(message, { id: `query-error:${message}` })
  },
  onSuccess: (_data, query) => {
    reportedFailures.delete(query.queryHash)
  },
})

// A query removed from the cache starts over: logout's queryClient.clear() removes every
// query, and the page is not reloaded, so without this a query that failed before a logout
// and never succeeded stayed marked as reported, and its first failure after signing back in
// showed nothing.
queryCache.subscribe((event) => {
  if (event.type === 'removed') reportedFailures.delete(event.query.queryHash)
})

export const queryClient = new QueryClient({
  queryCache,
  defaultOptions: {
    queries: {
      retry: retryOnlyTransient,
      // Without this, a sustained outage retries forever instead of the three times above.
      //
      // `MainContent` swaps the whole routed tree for "Loading..." while the device or
      // account query is fetching. A query that never succeeds stays `isPending`, so every
      // retry cycle reads as an initial fetch: the tree unmounts, the retries exhaust,
      // `isLoading` flips false, the tree remounts, its fresh observers see a query with no
      // data, and the cycle restarts — roughly every 16 s, indefinitely.
      //
      // `retryOnMount: false` breaks it: an already-failed query does not refetch just
      // because an observer mounted. Recovery still happens on window refocus
      // (`refetchOnWindowFocus` is on by default) or a reload.
      retryOnMount: false,
    },
  },
})
